import { Filter } from 'lucide-react'
import { cn } from '@lib/utils/helpers'
import type { ListFiltersButtonProps } from './ListFiltersButton.types'

/**
 * Shared Filters control: neutral when idle, primary outline + count badge when active.
 * Used by report toolbars and list pages (ListSearchToolbar).
 */
export function ListFiltersButton({
  onClick,
  activeFilterCount = 0,
  testIdPrefix = 'list',
  className,
  label = 'Filters',
}: ListFiltersButtonProps) {
  const hasFilters = activeFilterCount > 0

  return (
    <button
      type="button"
      data-testid={`${testIdPrefix}-filters-open-button`}
      onClick={onClick}
      aria-label={
        hasFilters
          ? `${label}, ${activeFilterCount} active filter${activeFilterCount === 1 ? '' : 's'}`
          : label
      }
      className={cn(
        'relative inline-flex flex-1 items-center justify-center gap-2 rounded-xl border bg-white px-5 py-2.5 text-sm font-semibold shadow-sm transition-all hover:shadow-md md:flex-none',
        hasFilters
          ? 'border-[rgb(21_151_255)] text-[rgb(21_151_255)] hover:bg-[rgb(21_151_255)]/5'
          : 'border-slate-200 text-slate-700 hover:bg-slate-50',
        className,
      )}
    >
      <Filter
        className={cn(
          'h-[18px] w-[18px] shrink-0',
          hasFilters ? 'text-[rgb(21_151_255)]' : 'text-slate-600',
        )}
        aria-hidden
      />
      <span>{label}</span>
      {hasFilters ? (
        <span
          data-testid={`${testIdPrefix}-filters-active-count-badge`}
          className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[rgb(21_151_255)] px-1 text-xs font-bold leading-none text-white tabular-nums"
          aria-hidden
        >
          {activeFilterCount > 99 ? '99+' : activeFilterCount}
        </span>
      ) : null}
    </button>
  )
}
